/**
 * 高德地图组件（AMap） — React 包装 + 增强版。
 *
 * 用法：
 *   <AMap
 *     center={[lng, lat]}
 *     markers={[{position: [lng, lat], icon: '/static/map/start.png', label: '起点'}, ...]}
 *     polyline={[[lng, lat], ...]}        // 可选：自定义折线（如骑手轨迹）
 *     routeFrom={[lng, lat]}              // 可选：驾车路径规划起点
 *     routeTo={[lng, lat]}                // 可选：驾车路径规划终点
 *     height="320px"
 *   />
 *
 * 阶段 15（实时追踪）：
 *   - markers / polyline / routeFrom / routeTo 变化时**增量更新**：
 *     marker 用 moveTo 平滑移动、polyline 用 setPath、路径按需重算
 *   - 不再因 props 变化销毁重建地图
 *
 * 加载策略：
 *   - 第一次调用时动态加载 AMap JS API（通过 <script> 注入）
 *   - 启用安全密钥（自 v2.0 起必填）
 *   - 未配置 KEY 时显示占位 UI，不影响其他功能
 */

"use client";

import { useEffect, useRef, useState } from "react";
import { loadAMap } from "@/lib/amap-loader";

export type MapMarker = {
  position: [number, number] | readonly [number, number] | readonly number[]; // [lng, lat]
  title?: string;
  icon?: string; // 图片路径
  /** 图标显示尺寸 [宽, 高]（px），默认 [36, 45]（pin 造型） */
  iconSize?: [number, number];
  /** 图标锚点：bottom（pin 尖端对准坐标，默认）或 center（圆心对准坐标，如骑手徽章） */
  anchor?: "bottom" | "center";
  label?: string;
  /** 自定义 HTML 内容（覆盖默认 icon 渲染）*/
  content?: string;
  /** 标记 z-index */
  zIndex?: number;
  /** 位置更新时是否平滑移动（默认 true，如骑手 marker）*/
  animate?: boolean;
};

/** icon 模式生成的 content HTML（供位置不变但 label/icon 变化时刷新） */
function iconContent(m: MapMarker): string | null {
  if (m.content) return m.content;
  if (m.icon) {
    const [w, h] = m.iconSize ?? [26, 32];
    return `<div style="display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
      <img src="${m.icon}" style="width:${w}px;height:${h}px;" />
      ${m.label ? `<div style="background:rgba(0,0,0,0.85);color:white;padding:2px 6px;border-radius:4px;font-size:11px;margin-top:2px;white-space:nowrap;">${m.label}</div>` : ""}
    </div>`;
  }
  return null;
}

function createMarker(AMapObj: any, map: any, m: MapMarker): any {
  const markerOpts: any = {
    position: m.position as any,
    title: m.title,
    map,
    zIndex: m.zIndex || 100,
  };
  const content = iconContent(m);
  if (content) {
    markerOpts.content = content;
  }
  if (m.icon) {
    const [w, h] = m.iconSize ?? [26, 32];
    // bottom: pin 尖端（左上角在 (-w/2, -h)）；center: 圆心对准坐标
    markerOpts.offset =
      m.anchor === "center"
        ? new AMapObj.Pixel(-w / 2, -h / 2)
        : new AMapObj.Pixel(-w / 2, -h);
  }
  return new AMapObj.Marker(markerOpts);
}

/** 两个经纬度是否相同（5 位小数 ≈ 1m 精度） */
function samePos(a: any, b: any): boolean {
  if (!a || !b) return false;
  return (
    Math.abs((a.lng ?? a[0]) - b[0]) < 1e-5 &&
    Math.abs((a.lat ?? a[1]) - b[1]) < 1e-5
  );
}

/** 路径规划起点的重算阈值：移动超过约 50 米才重新规划 */
const ROUTE_RECALC_DEG = 5e-4;

export function AMap({
  center = [116.45, 39.9],
  markers = [],
  polyline,
  routeFrom,
  routeTo,
  height = "320px",
  zoom = 13,
  className,
  showControls = true,
  fitView = true,
  onMapClick,
  pickMode = false,
  pinIcon,
}: {
  center?: [number, number];
  markers?: MapMarker[];
  polyline?: Array<[number, number]>;
  /** 驾车路径规划起点（与 routeTo 配对）*/
  routeFrom?: [number, number];
  /** 驾车路径规划终点 */
  routeTo?: [number, number];
  height?: number | string;
  zoom?: number;
  className?: string;
  showControls?: boolean;
  fitView?: boolean;
  /** 选点回调：pickMode 时为拖动结束触发，否则为点击/长按触发 */
  onMapClick?: (lng: number, lat: number) => void;
  /** 选点模式：中心固定 pin + 拖动地图 moveend 取中心坐标回调 */
  pickMode?: boolean;
  /** 中心 pin 图标（pickMode 时显示），默认 /static/map/start.png */
  pinIcon?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const drivingRef = useRef<any>(null);
  const markersRef = useRef<Map<number, any>>(new Map()); // index -> marker 实例
  const polylineRef = useRef<any>(null);
  const didFitRef = useRef(false);
  const lastRouteFromRef = useRef<[number, number] | null>(null);
  const lastCenterRef = useRef<[number, number] | null>(null);
  /** setCenter 程序移动后跳过一次 moveend 回调（pickMode 用） */
  const ignoreMoveendRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [keyMissing, setKeyMissing] = useState(false);
  const [ready, setReady] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{
    distance: number;
    duration: number;
  } | null>(null);

  // ---------- 初始化：只创建一次地图 ----------
  useEffect(() => {
    let mounted = true;

    loadAMap()
      .then((AMap) => {
        if (!ref.current || !mounted) return;
        const map = new AMap.Map(ref.current, {
          center,
          zoom,
          viewMode: "2D",
          // 隐藏默认 logo / 缩放
          features: ["bg", "point", "road", "building"],
          // 显式开启触摸/鼠标拖拽 + 缩放（不同 SDK 版本默认行为不一致，显式更稳）
          dragEnable: true,
          zoomEnable: true,
          doubleClickZoom: true,
          scrollWheel: true,
          touchZoom: true,
        });
        map.on("error", (e: any) => {
          // 高德内部 tile 加载失败（ERR_PROXY_CONNECTION_FAILED 等）只控制台告警，不影响地图渲染
          // eslint-disable-next-line no-console
          console.warn("[AMap] error:", e);
        });
        mapRef.current = map;
        // 初始化时记录 center，避免初次 markers 同步触发多余 setCenter
        if (center && Array.isArray(center) && center.length === 2) {
          lastCenterRef.current = [center[0], center[1]];
        }

        if (showControls) {
          // 工具栏（缩放/平移）
          map.addControl(new AMap.ToolBar({ position: "RB" }));
          // 比例尺
          map.addControl(new AMap.Scale({ position: "LB" }));
        }

        // 选点模式：
        //  - pickMode（美团式）：中心固定 pin，拖动地图 moveend 后取中心坐标回调
        //  - 否则：点击 / 长按地图选点（旧逻辑保留）
        if (onMapClick) {
          if (pickMode) {
            // 美团式：只在 moveend（拖动停止）时触发一次，避免拖动过程中频繁调用高德
            let timer: ReturnType<typeof setTimeout> | null = null;
            const emitCenter = () => {
              if (timer) clearTimeout(timer);
              timer = setTimeout(() => {
                // setCenter（程序移动）触发的 moveend 不回调，只响应用户拖动
                if (ignoreMoveendRef.current) {
                  ignoreMoveendRef.current = false;
                  return;
                }
                const c = map.getCenter();
                if (c && typeof c.getLng === "function") {
                  onMapClick(c.getLng(), c.getLat());
                }
              }, 300); // 300ms 防抖：用户停止移动后才请求一次
            };
            map.on("moveend", emitCenter);
          } else {
            // 单击（PC）
            map.on("click", (e: any) => {
              const ll = e.lnglat;
              if (ll && typeof ll.getLng === "function") {
                onMapClick(ll.getLng(), ll.getLat());
              }
            });
            // 移动端 longpress
            (map as any).on("longpress", (e: any) => {
              const ll = e.lnglat;
              if (ll && typeof ll.getLng === "function") {
                onMapClick(ll.getLng(), ll.getLat());
              }
            });
          }
        }

        setReady(true);
      })
      .catch((e) => {
        if (!mounted) return;
        const msg = `地图加载失败: ${e?.message || e}`;
        setError(msg);
        if (msg.includes("未配置")) setKeyMissing(true);
      });

    return () => {
      mounted = false;
      markersRef.current.clear();
      polylineRef.current = null;
      didFitRef.current = false;
      lastRouteFromRef.current = null;
      lastCenterRef.current = null;
      if (drivingRef.current) {
        drivingRef.current.clear();
        drivingRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- markers 增量同步（阶段 15：骑手 marker 平滑移动） ----------
  useEffect(() => {
    if (!ready || !mapRef.current || !window.AMap) return;
    const AMapObj = window.AMap;

    // center 变化 → 同步地图中心（如选区后地图跳到新区域）
    // 用 lastCenterRef 缓存上次应用的中心，避免跟 marker 同步打架
    if (center && Array.isArray(center) && center.length === 2) {
      const wantLng = center[0];
      const wantLat = center[1];
      const last = lastCenterRef.current;
      const moved =
        !last ||
        Math.abs(last[0] - wantLng) > 1e-5 ||
        Math.abs(last[1] - wantLat) > 1e-5;
      if (moved) {
        // 程序移动地图：若实际位置会变化，标记跳过 moveend 回调，避免 pickMode 下误触发选点
        // （目标位置与当前位置相同时 setCenter 不产生 moveend，不置标志，防止吞掉用户后续拖动）
        const cur = mapRef.current.getCenter();
        const actuallyMoves =
          !cur ||
          Math.abs(cur.getLng() - wantLng) > 1e-5 ||
          Math.abs(cur.getLat() - wantLat) > 1e-5;
        if (actuallyMoves) ignoreMoveendRef.current = true;
        mapRef.current.setCenter([wantLng, wantLat]);
        lastCenterRef.current = [wantLng, wantLat];
      }
    }

    markers.forEach((m, i) => {
      const pos = new AMapObj.LngLat(m.position[0], m.position[1]);
      const existing = markersRef.current.get(i);
      if (!existing) {
        markersRef.current.set(i, createMarker(AMapObj, mapRef.current, m));
        return;
      }
      if (samePos(existing.getPosition(), pos)) {
        // 位置没变，但 label/icon/content 可能变了（如"定位中..."→"你附近有 3 个骑手"）
        const want = iconContent(m);
        if (want && typeof existing.getContent === "function") {
          const cur = existing.getContent();
          if (typeof cur !== "string" || cur !== want) existing.setContent(want);
        }
        return;
      }
      if (m.animate !== false && typeof existing.moveTo === "function") {
        // 平滑移动：按两点距离动态算速度，让动画约 8s 走完（匹配 10s 轮询节奏）
        const from = existing.getPosition();
        let speed = 40;
        if (from) {
          // 近似：1 纬度 ≈ 111km
          const distM =
            Math.hypot(pos[0] - from.getLng(), pos[1] - from.getLat()) * 111000;
          speed = Math.min(200, Math.max(5, (distM / 8) * 3.6)); // m/s → km/h
        }
        existing.moveTo(pos, speed);
      } else {
        existing.setPosition(pos);
      }
    });

    // 移除多余 marker
    for (const key of Array.from(markersRef.current.keys())) {
      if (key >= markers.length) {
        markersRef.current.get(key).setMap(null);
        markersRef.current.delete(key);
      }
    }

    // 首次出现覆盖物时自适应视野（仅一次，避免轮询期间地图跳动）
    if (fitView && !didFitRef.current && (markers.length > 0 || (polyline && polyline.length > 0))) {
      didFitRef.current = true;
      setTimeout(() => mapRef.current?.setFitView(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, polyline, center, ready]);

  // ---------- polyline 增量同步（骑手轨迹） ----------
  useEffect(() => {
    if (!ready || !mapRef.current || !window.AMap) return;
    const AMapObj = window.AMap;
    if (!polyline || polyline.length < 2) return;

    if (!polylineRef.current) {
      polylineRef.current = new AMapObj.Polyline({
        path: polyline,
        strokeColor: "#08B5D2",
        strokeWeight: 6,
        strokeOpacity: 0.8,
        lineJoin: "round",
        lineCap: "round",
        showDir: true,
        map: mapRef.current,
      });
    } else {
      polylineRef.current.setPath(polyline);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polyline, ready]);

  // ---------- 驾车路径规划：起点移动超过阈值才重算 ----------
  useEffect(() => {
    if (!ready || !mapRef.current || !window.AMap) return;
    const AMapObj = window.AMap;
    if (!routeFrom || !routeTo) return;

    const last = lastRouteFromRef.current;
    const movedFar =
      !last ||
      Math.abs(last[0] - routeFrom[0]) > ROUTE_RECALC_DEG ||
      Math.abs(last[1] - routeFrom[1]) > ROUTE_RECALC_DEG;
    if (!movedFar) return;
    lastRouteFromRef.current = [routeFrom[0], routeFrom[1]];

    if (!drivingRef.current) {
      drivingRef.current = new AMapObj.Driving({
        map: mapRef.current,
        autoFitView: false, // 轮询期间不反复缩放
        hideMarkers: true, // 隐藏默认的起终点标记（用我们自己的 marker）
      });
    } else {
      drivingRef.current.clear();
    }
    drivingRef.current.search(
      new AMapObj.LngLat(routeFrom[0], routeFrom[1]),
      new AMapObj.LngLat(routeTo[0], routeTo[1]),
      (status: string, result: any) => {
        if (status === "complete" && result.routes?.[0]) {
          const route = result.routes[0];
          setRouteInfo({
            distance: route.distance, // 米
            duration: route.time, // 秒
          });
        } else {
          console.warn("[AMap] driving search failed:", status, result);
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeFrom, routeTo, ready]);

  return (
    <div
      className={`relative overflow-hidden rounded-lg ${className || ""}`}
      style={{ height }}
    >
      <div ref={ref} className="w-full h-full" />

      {/* 选点模式：中心固定 pin（美团式） */}
      {pickMode && ready && !error && (
        <div
          className="absolute left-1/2 top-1/2 pointer-events-none z-[300]"
          style={{ transform: "translate(-50%, -100%)" }}
        >
          <div className="flex flex-col items-center">
            <img
              src={pinIcon || "/static/map/start.svg"}
              alt="选点"
              className="w-7 h-9 drop-shadow-lg"
            />
          </div>
        </div>
      )}

      {keyMissing && (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary text-center p-4">
          <div>
            <div className="text-3xl mb-2">🗺️</div>
            <div className="text-sm tn-text-secondary">
              未配置高德地图 Key
              <br />
              请在管理后台「地图配置」中设置
            </div>
          </div>
        </div>
      )}

      {error && !keyMissing && (
        <div className="absolute bottom-2 left-2 right-2 bg-danger-bg border border-danger rounded p-2 text-xs text-danger">
          {error}
        </div>
      )}

      {routeInfo && (
        <div className="absolute top-2 left-2 bg-white/95 px-3 py-2 rounded-lg shadow-sm text-xs">
          <div className="flex items-center gap-3">
            <span className="text-main font-medium">
              {(routeInfo.distance / 1000).toFixed(1)} km
            </span>
            <span className="tn-text-secondary">|</span>
            <span className="tn-text-secondary">
              {Math.round(routeInfo.duration / 60)} 分钟
            </span>
          </div>
        </div>
      )}

      {ready && !error && (
        <div className="absolute bottom-2 right-2 text-xs tn-text-secondary bg-white/80 px-2 py-1 rounded">
          高德 AMap v2.0
        </div>
      )}
    </div>
  );
}
